import { Model, Factory, trait, belongsTo, hasMany } from 'miragejs'
import type { ModelDefinition, ModelInstance } from 'miragejs/-types'
import {
  rand,
  randChanceBoolean,
  randBoolean,
  randRecentDate,
  randParagraph,
  randUuid,
} from '@ngneat/falso'

import {
  SearchDepartmentFilter,
  type MergedSettingsData,
} from '@/models/settings.js'
import { LabelPrintingSolutions } from '@/composables/labelPrinting.js'
import type { MockServer } from './server.js'
import type { DepartmentData } from './Department.js'

export const model = (Model as ModelDefinition<MergedSettingsData>).extend({
  activeDepartment: belongsTo('department'),
  availableDepartments: hasMany('department'),
  homeDepartment: belongsTo('department'),
})

export const factory = Factory.extend<MergedSettingsData>({
  super_search_department_filter() {
    return rand(Object.values(SearchDepartmentFilter))
  },
  active_department_checking() {
    return randChanceBoolean({ chanceTrue: 0.9 })
  },
  credit_note_first_possible_date() {
    return randRecentDate().toISOString()
  },
  cross_department_prepayments_enabled() {
    return randChanceBoolean({ chanceTrue: 0.9 })
  },
  allow_prepayment() {
    return randChanceBoolean({ chanceTrue: 0.9 })
  },
  prevent_payments_after_fully_paid() {
    return randChanceBoolean({ chanceTrue: 0.5 })
  },
  policlinical_multiple_notes() {
    return randChanceBoolean({ chanceTrue: 0.9 })
  },
  credit_note_invoice_date_logic() {
    return rand([
      'CREDIT_NOTE_DATE_LOGIC_CURRENT_EDITABLE',
      'CREDIT_NOTE_DATE_LOGIC_ORIGINAL_EDITABLE',
      'CREDIT_NOTE_DATE_LOGIC_CURRENT_NON_EDITABLE',
    ])
  },
  enable_credit_note_transfer_to_prepayments() {
    return randChanceBoolean({ chanceTrue: 0.9 })
  },
  enable_payment_cancellation() {
    return randChanceBoolean({ chanceTrue: 0.9 })
  },
  enable_payment_to_prepayments() {
    return randChanceBoolean({ chanceTrue: 0.9 })
  },
  insurance_auto_refund_payments() {
    return randChanceBoolean({ chanceTrue: 0.9 })
  },
  invoice_country() {
    return rand(['GB', 'NL', 'FI', 'US'])
  },
  invoice_country_region() {
    return rand(['GB-ENG', 'NL-NH', 'FI-19', 'US-CA'])
  },
  invoice_rounding_button() {
    return rand([
      'ROUNDING_DISABLED',
      'ROUNDING_005',
      'ROUNDING_010',
      'ROUNDING_050',
      'ROUNDING_100',
    ])
  },
  invoice_rounding_method() {
    return rand([
      'ROUNDING_DISABLED',
      'ROUNDING_005',
      'ROUNDING_010',
      'ROUNDING_050',
      'ROUNDING_100',
    ])
  },
  invoice_whole_numbers() {
    return randChanceBoolean({ chanceTrue: 0.9 })
  },
  organization_financial_period_closing() {
    return randChanceBoolean({ chanceTrue: 0.9 })
  },
  organization_financial_period_closing_date() {
    return randRecentDate().toISOString()
  },
  payment_cancel_set_date() {
    return randChanceBoolean({ chanceTrue: 0.9 })
  },
  payment_to_prepayments_set_date() {
    return randChanceBoolean({ chanceTrue: 0.9 })
  },
  prepayment_refund_set_date() {
    return randChanceBoolean({ chanceTrue: 0.9 })
  },
  same_quantity_linked_parent() {
    return randChanceBoolean({ chanceTrue: 0.9 })
  },
  stock_department_sharing() {
    return randChanceBoolean({ chanceTrue: 0.9 })
  },
  stock_locations_in_use() {
    return randChanceBoolean({ chanceTrue: 0.9 })
  },
  vat_calculation_method() {
    return rand([
      'VAT_CALCULATION_BY_ITEM',
      'VAT_CALCULATION_BY_INVOICE_ROW',
      'VAT_CALCULATION_BY_TOTAL',
    ])
  },
  vat_determining_type() {
    return rand(['VAT_DETERMINING_INCL_VAT', 'VAT_DETERMINING_EXCL_VAT'])
  },
  vat_prices() {
    return rand(['VAT_PRICES_CHOICE_INCLUDED', 'VAT_PRICES_CHOICE_EXCLUDED'])
  },
  currency_code() {
    return rand(['EUR', 'USD', 'SEK', 'NOK', 'DKK'])
  },
  currency_locale() {
    return rand(['fi-FI', 'en-US', 'fr-FR', 'de-DE'])
  },
  tax_text() {
    return 'VAT'
  },
  enable_cancellation_email_sending() {
    return randBoolean()
  },
  boarding_enabled() {
    return true
  },
  automatically_open_new_clinical_note() {
    return randBoolean()
  },
  default_clinical_note() {
    return randParagraph()
  },
  email() {
    return 'donotreply@vogonclinic.com'
  },
  first_day_of_week() {
    return this.currency_locale === 'en-US'
      ? 'WEEKDAY_SUNDAY'
      : 'WEEKDAY_MONDAY'
  },
  provet_theme() {
    return rand(['DEFAULT', 'ACCESSIBILITY'])
  },
  client_title_available() {
    return randBoolean()
  },
  socketly_ws_url() {
    return 'ws://localhost:4000'
  },
  socketly_token() {
    return randUuid()
  },
  vetenvoy_microchipping_enabled() {
    return false
  },
  user_default_language() {
    return rand(['en', 'fi', 'fr', 'de'])
  },
  client_interaction_language() {
    return rand(['en', 'fi', 'fr', 'de'])
  },
  use_client_type() {
    return randBoolean()
  },
  use_enhanced_diagnostic_imaging_workflow() {
    return false
  },
  weight_unit() {
    return rand(['lbs', 'kg'])
  },
  reporting_dimension_1_name() {
    return 'Cost center'
  },
  reporting_dimension_2_name() {
    return 'Project code'
  },
  reporting_dimension_3_name() {
    return 'Reporting dimension 3'
  },
  label_printing_enabled() {
    return rand(Object.values(LabelPrintingSolutions))
  },
  microchip_date_required() {
    return randBoolean()
  },
  support_communications() {
    return 0
  },
  central_laboratory_enabled() {
    return true
  },
  microbiology_enabled() {
    return true
  },
  limit_adding_items() {
    return false
  },
  use_central_item_list() {
    return randBoolean()
  },
  show_external_lab_filter() {
    return true
  },
  show_account_numbers() {
    return randBoolean()
  },
  require_account_numbers() {
    return randBoolean()
  },
  afterCreate(mergedSettings, server) {
    // Set up department relationships similar to User model
    if (!mergedSettings.availableDepartments.length) {
      const allDepartments = server.schema.all('department')
      const availableDepartments = allDepartments.filter(
        (department: ModelInstance) => {
          return !(department.attrs as DepartmentData).archived
        },
      )
      mergedSettings.update({ availableDepartments })
    }

    if (!mergedSettings.activeDepartment) {
      const nonArchivedDepartments = server.schema
        .all('department')
        .filter(
          (department: ModelInstance) =>
            !(department.attrs as DepartmentData).archived,
        )
      const activeDepartment =
        nonArchivedDepartments.models[0] || server.schema.first('department')
      mergedSettings.update({ activeDepartment })
    }

    // Set home department to the last department (different from active for testing)
    if (!mergedSettings.homeDepartment) {
      const nonArchivedDepartments = server.schema
        .all('department')
        .filter(
          (department: ModelInstance) =>
            !(department.attrs as DepartmentData).archived,
        ).models
      if (nonArchivedDepartments.length > 1) {
        mergedSettings.update({
          homeDepartment:
            nonArchivedDepartments[nonArchivedDepartments.length - 1],
        })
      } else {
        mergedSettings.update({
          homeDepartment:
            nonArchivedDepartments[0] || server.schema.first('department'),
        })
      }
    }

    // Department ID 1 (Bath Clinic) is the central department for testing
    const CENTRAL_DEPARTMENT_ID = '1'
    const isCentral =
      mergedSettings.activeDepartment?.id === CENTRAL_DEPARTMENT_ID
    mergedSettings.update({ is_central_department: isCentral })
  },

  withFullPaymentAndPrepaymentModification: trait({
    afterCreate(setting, _server) {
      setting.update({
        enable_payment_cancellation: true,
        enable_payment_to_prepayments: true,
        enable_credit_note_transfer_to_prepayments: true,
      })
    },
  }),

  withVetEnvoyEnabled: trait({
    afterCreate(setting, _server) {
      setting.update({ vetenvoy_microchipping_enabled: true })
    },
  }),
})

export function routes(server: MockServer) {
  server.get('/settings/merged', (schema) => schema.first('mergedSettings'))
}

export function seeds(server: MockServer) {
  server.create(
    'mergedSettings',
    {
      super_search_department_filter: 'THIS_DEPARTMENT',
      provet_theme: 'DEFAULT',
      show_account_numbers: true,
      require_account_numbers: false,
      invoice_country: 'FI',
    },
    'withFullPaymentAndPrepaymentModification',
    'withVetEnvoyEnabled',
  )
}
